// GastosView.js — Facturas → Gastos: bandeja de facturas de proveedor
// recibidas por correo (ver procesarBandejaGastos.js), con botones para
// generar el gasto con IA y guardarlo o descartarlo.
import React, { useState, useEffect, useCallback } from 'react';
import { authService } from '../../services/authService';
import GeneradorGastoModal from './GeneradorGastoModal';
import RemitentesAutorizadosPanel from './RemitentesAutorizadosPanel';

const ESTADO_LABEL = {
  pendiente:      { label: 'Pendiente',              color: 'bg-gray-100 text-gray-600' },
  propuesto:      { label: 'Propuesto',               color: 'bg-blue-100 text-blue-700' },
  registrado:     { label: 'Registrado',              color: 'bg-emerald-100 text-emerald-700' },
  descartado:     { label: 'Descartado',              color: 'bg-gray-100 text-gray-400' },
  duplicado:      { label: 'Duplicado',               color: 'bg-amber-100 text-amber-700' },
  no_autorizado:  { label: 'Remitente no autorizado', color: 'bg-red-100 text-red-600' },
  error:          { label: 'Error',                   color: 'bg-red-100 text-red-600' },
};

function fmtEur(n) {
  if (n == null || isNaN(n)) return '—';
  return Number(n).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}
function fmtFechaHora(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function GastosView() {
  const user = authService.getCurrentUser();
  const esAdmin = user?.role === 'admin';

  const [elementos, setElementos] = useState([]);
  const [pendientes, setPendientes] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState('');
  const [mesFiltro, setMesFiltro] = useState('');
  const [modalItem, setModalItem] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [comprobando, setComprobando] = useState(false);
  const [comprobarMsg, setComprobarMsg] = useState('');
  const [mostrarConfig, setMostrarConfig] = useState(false);

  const cargar = useCallback(() => {
    setLoading(true); setError('');
    const params = new URLSearchParams();
    if (estadoFiltro) params.set('estado', estadoFiltro);
    if (mesFiltro) params.set('mes', mesFiltro);
    fetch(`/.netlify/functions/getGastosBandeja?${params}`, { headers: { ...authService.authHeader() } })
      .then(r => r.json())
      .then(data => { if (!data.success) { setError(data.error); return; } setElementos(data.elementos); setPendientes(data.pendientes); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [estadoFiltro, mesFiltro]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleComprobarBuzon = async () => {
    setComprobando(true); setComprobarMsg('');
    try {
      const res = await fetch('/.netlify/functions/forzarProcesarBandejaGastos', { method: 'POST', headers: { 'Content-Type': 'application/json', ...authService.authHeader() } });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      setComprobarMsg(`${data.pdfsCreados} PDF nuevos de ${data.procesados} correos procesados${data.noAutorizados ? ` (${data.noAutorizados} de remitente no autorizado)` : ''}.`);
      cargar();
    } catch (e) {
      setComprobarMsg('Error: ' + e.message);
    } finally {
      setComprobando(false);
    }
  };

  const handleDescartar = async (id) => {
    if (!window.confirm('¿Descartar este elemento? No es una factura.')) return;
    try {
      const res = await fetch('/.netlify/functions/descartarGastoBandeja', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ id, estado: 'descartado' }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      cargar();
    } catch (e) {
      setError(e.message);
    }
  };

  if (mostrarConfig) {
    return (
      <div className="space-y-3">
        <button onClick={() => setMostrarConfig(false)} className="text-xs font-bold text-gray-500 hover:text-gray-700">← Volver a la bandeja</button>
        <RemitentesAutorizadosPanel />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <h2 className="font-bold text-gray-900">Gastos de proveedor</h2>
          {pendientes > 0 && (
            <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-red-100 text-red-600">{pendientes} pendiente{pendientes !== 1 ? 's' : ''}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {comprobarMsg && <span className="text-xs text-gray-500">{comprobarMsg}</span>}
          {esAdmin && (
            <>
              <button onClick={() => setMostrarConfig(true)} className="px-3 py-2 bg-gray-100 text-gray-700 text-xs font-bold rounded-xl hover:bg-gray-200">
                Remitentes autorizados
              </button>
              <button onClick={handleComprobarBuzon} disabled={comprobando} className="px-3 py-2 bg-red-600 text-white text-xs font-bold rounded-xl hover:bg-red-700 disabled:opacity-60">
                {comprobando ? 'Comprobando…' : 'Comprobar buzón ahora'}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="flex gap-2">
        <select value={estadoFiltro} onChange={e => setEstadoFiltro(e.target.value)} className="border rounded-lg px-3 py-2 text-xs">
          <option value="">Todos los estados</option>
          {Object.entries(ESTADO_LABEL).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <input type="month" value={mesFiltro} onChange={e => setMesFiltro(e.target.value)} className="border rounded-lg px-3 py-2 text-xs" />
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-2 text-sm">{error}</div>}

      <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-500" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b bg-gray-50/60">
                  <th className="text-left px-4 py-2 font-bold text-gray-500 uppercase">Recibido</th>
                  <th className="text-left px-3 py-2 font-bold text-gray-500 uppercase">Proveedor / remitente</th>
                  <th className="text-left px-3 py-2 font-bold text-gray-500 uppercase">Archivo</th>
                  <th className="text-left px-3 py-2 font-bold text-gray-500 uppercase">Estado</th>
                  <th className="text-right px-3 py-2 font-bold text-gray-500 uppercase">Fecha factura</th>
                  <th className="text-right px-3 py-2 font-bold text-gray-500 uppercase">Total</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {elementos.map(el => {
                  const estado = ESTADO_LABEL[el.estado] || { label: el.estado, color: 'bg-gray-100 text-gray-500' };
                  const puedeOperar = ['pendiente', 'propuesto', 'error'].includes(el.estado);
                  return (
                    <tr key={el.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-gray-500 whitespace-nowrap">{fmtFechaHora(el.fechaRecepcion)}</td>
                      <td className="px-3 py-2 text-gray-700">
                        <button className="font-semibold hover:underline text-left disabled:no-underline" disabled={!el.pdfUrl} onClick={() => setPreviewUrl(el.pdfUrl)}>
                          {el.remitenteOriginal || el.remitente}
                        </button>
                        {el.remitenteOriginal && <p className="text-[10px] text-gray-400">reenviado por {el.remitente}</p>}
                      </td>
                      <td className="px-3 py-2 text-gray-500 max-w-[200px] truncate" title={el.nombreArchivo}>{el.nombreArchivo}</td>
                      <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${estado.color}`}>{estado.label}</span></td>
                      <td className="px-3 py-2 text-right text-gray-500">{el.propuesta?.fecha || '—'}</td>
                      <td className="px-3 py-2 text-right font-semibold text-gray-700">{el.propuesta?.total != null ? fmtEur(el.propuesta.total) : '—'}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {puedeOperar && (
                          <>
                            <button onClick={() => setModalItem(el)} className="text-red-600 hover:underline mr-2 font-semibold">Generar gasto</button>
                            <button onClick={() => handleDescartar(el.id)} className="text-gray-400 hover:underline">Descartar</button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {elementos.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-8 text-gray-400">Sin elementos en la bandeja</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modalItem && (
        <GeneradorGastoModal item={modalItem} onClose={() => setModalItem(null)} onSaved={() => { setModalItem(null); cargar(); }} />
      )}

      {previewUrl && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setPreviewUrl(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl h-[85vh]" onClick={e => e.stopPropagation()}>
            <iframe title="PDF" src={previewUrl} className="w-full h-full rounded-2xl" />
          </div>
        </div>
      )}
    </div>
  );
}
